import { useCallback, useContext, useState } from "react";
import { useTranslation } from "react-i18next";

import MuiAutocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";

import FormContext from "./FormContext";
import { SelectableObject } from "./types";

interface BaseAutocompleteProps {
  label: string;
  items: SelectableObject[];
  selected?: SelectableObject | SelectableObject[] | null | string;
  required?: boolean;
  // With freeSolo, a multiple selection can mix typed strings and options.
  onChange: (value: SelectableObject | (SelectableObject | string)[] | null | string) => void;
  onBlur?: (e: any) => void;
  onInputChange?: (event: any, value: string) => void;
  multiple: boolean;
  disableClearable: boolean;
  freeSolo?: boolean;
  formatLabel?: (item: any) => string;
  error?: string;
  sx?: Record<string, any>;
}

const BaseAutocomplete = ({
  label,
  items,
  selected,
  required,
  onChange,
  onInputChange,
  onBlur,
  multiple,
  disableClearable,
  freeSolo = false,
  formatLabel,
  error,
  ...rest
}: BaseAutocompleteProps) => {
  const { t } = useTranslation();

  const getOptionLabel = (option: any) => {
    if (typeof option === "string") return option;
    return formatLabel ? formatLabel(option) : option?.getDisplayName ? option.getDisplayName(t) : "";
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (freeSolo && event.key === "Enter") {
      // Close the dropdown on Enter in freeSolo mode
      (event.target as HTMLElement).blur();
      setTimeout(() => {
        (event.target as HTMLElement).focus();
      }, 0);
    }
  };

  return (
    // TODO: think about to changing disableClearable to required and require default value in that case
    <MuiAutocomplete
      size="small"
      options={items || []}
      onChange={(_, v) => onChange(v)}
      onInputChange={onInputChange}
      value={selected || (multiple ? [] : null)}
      getOptionLabel={getOptionLabel}
      multiple={multiple}
      disableClearable={disableClearable}
      freeSolo={freeSolo}
      autoHighlight={!freeSolo} // Don't auto-highlight in freeSolo mode
      renderInput={(params) => (
        <TextField
          {...params}
          variant="outlined"
          label={label}
          error={!!error}
          onKeyDown={handleKeyDown}
          onBlur={onBlur}
        />
      )}
      {...rest}
    />
  );
};

export interface AutocompleteProps {
  label: string;
  items: SelectableObject[];
  selected?: SelectableObject | SelectableObject[] | null | string;
  required?: boolean;
  onChange: (value: any) => void;
  onInputChange?: (event: any, value: string) => void;
  onBlur?: (e: any) => void;
  multiple?: boolean;
  disableClearable?: boolean;
  freeSolo?: boolean;
  // formatLabel is a legacy property for backward compatibility, use getDisplayName in new code instead
  formatLabel?: (item: any) => string;
  sx?: Record<string, any>;
}

export const Autocomplete = ({
  label,
  selected,
  required,
  multiple = false,
  disableClearable = false,
  freeSolo = false,
  onChange,
  onInputChange,
  onBlur,
  ...rest
}: AutocompleteProps) => {
  const [requiredError, setRequiredError] = useState<string | undefined>(undefined);

  const handleRequired = useCallback(
    (e: any) => {
      if (!required) {
        return;
      }
      const hasValue = selected || e?.id || e?.target?.innerText || (typeof e === "string" && e);
      if (hasValue) {
        setRequiredError(undefined);
      } else {
        setRequiredError(label);
      }
    },
    [required, selected, label],
  );

  return (
    <BaseAutocomplete
      label={label}
      multiple={multiple}
      disableClearable={disableClearable}
      freeSolo={freeSolo}
      onChange={(e) => {
        handleRequired(e);
        onChange(e);
      }}
      onInputChange={onInputChange}
      onBlur={(e) => {
        handleRequired(e);
        if (onBlur) onBlur(e);
      }}
      error={requiredError}
      selected={selected}
      {...rest}
    />
  );
};

export interface AutocompleteFormProps {
  name: string;
  label: string;
  items: SelectableObject[];
  multiple?: boolean;
  disableClearable?: boolean;
  sx?: Record<string, any>;
}

export const AutocompleteForm = ({
  name,
  multiple = false,
  disableClearable = false,
  ...rest
}: AutocompleteFormProps) => {
  const context = useContext(FormContext);
  const { value, error } = context.get(name);

  const change = (value: SelectableObject | (SelectableObject | string)[] | null | string) => {
    context.set(name, value);
  };

  return (
    <BaseAutocomplete
      selected={value}
      onChange={change}
      multiple={multiple}
      disableClearable={disableClearable}
      error={error}
      {...rest}
    />
  );
};

export default AutocompleteForm;
